import {
    type CommonMessageProperties,
    type ContentLike,
    type ContentPrune,
    type SearchResultContentParams,
    toAbsoluteSrc,
} from './types';
import {
    AfterOrderMessageApiAfterOrderMessageGet,
    BannerApiBannersGet,
    BannerPrivacyEnum,
    type Faq,
    FaqApiFaqsGet,
    type FaqFaqItemInner,
    HomepageApiHomepagesGet,
    type HomepageDataInnerOneOf,
    type HomepageDataInnerOneOf1,
    type HomepageDataInnerOneOf2,
    HomepageDataInnerOneOfComponentEnum,
    LandingContentsApiLandingContentsDetailsGet,
    type LandingContentsParentEnum,
    type LandingContentsProvidersInner,
    MessageApiMessagesGet,
    MessageSearchModeEnum,
    MusicListApiMusicListsGet,
    type SearchResultContent,
    SearchResultContentApiSearchResultContentsFullContentGet,
    SearchResultContentApiSearchResultContentsOrEmptyGet,
    type TermsAndConditions,
    TermsAndConditionsApiTermsAndConditionsGet,
    UtmCampaignApiUtmCampaignsGet,
} from '@clients/ContentApi';
import {
    AuthLocalStorageKeys,
    type UserCredential,
    UserType,
} from '@store/auth';
import {
    type ReserveType,
    type VerifyPathError,
    ViewRedirectError,
} from '@utils/itinerary';
import SiteBanner from '@store/content/entities/SiteBanner';
import SiteTopBanner from '@store/content/entities/SiteTopBanner';
import SiteUtmCampaign from '@store/content/entities/SiteUtmCampaign';
import LandingSection from '@store/content/entities/LandingSection';
import LandingContent from '@store/content/entities/LandingContent';
import LandingDistance from '@store/content/entities/LandingDistance';
import SiteMusic from '@store/content/entities/SiteMusic';
import SiteMessage from '@store/content/entities/SiteMessage';
import { type TripSearchForm } from '@search/trip/TripSearchForm';
import CityBanner from '@store/content/entities/CityBanner';
import { LandingCorporation } from '@store/content/entities/LandingCorporation';

export class ContentStore {
    //#region state
    isLoadingBanners = false;
    private banners: SiteBanner[] = [];
    utmCampaigns: SiteUtmCampaign[] = [];
    corporations: LandingCorporation[] = [];
    landingSections: LandingSection[] = [];
    providers: LandingContentsProvidersInner[] = [];
    providersTitle = '';
    private homepageBanners: SiteTopBanner[] = [];
    private homepageMessages: SiteMessage[] = [];
    private homepageSliders: SiteTopBanner[][] = [];
    homepageMessagesLoading = false;
    private homepageMessagesLoaded = false;
    landingPageContents: LandingContent | null = null;
    landingDistances: LandingDistance[] = [];
    landingPageContentLoading = false;
    private messages: SiteMessage[] = [];
    private closedMessageIds: string[] = [];
    private faqs: ContentPrune<Faq>[] = [];
    searchResultFaqs: FaqFaqItemInner[] = [];
    hotelCityBanner: CityBanner | null = null;
    hasLandingContents = false;
    private termsAndConditions: ContentPrune<TermsAndConditions>[] = [];

    /**
     * keep date of last loaded view content to not load multiple times
     */
    private lastLoadedViewContent: Date | null = null;
    searchResultContentsLoading = false;
    private lastLoadedSearchResultContents: SearchResultContentParams | null =
        null;

    searchResultContent = '';
    searchResultDescription = '';
    private musicList: SiteMusic[] = [];
    mounted = false;
    afterOrderMessage = '';
    //#endregion

    get landingCityName() {
        return this.landingPageContents?.cityName ?? '';
    }

    get landingProvinceName() {
        return this.landingPageContents?.provinceName ?? '';
    }

    get landingPageH1() {
        return this.landingPageContents?.h1 || null;
    }

    get landingPageTitle(): string {
        return this.landingPageContents?.title || '';
    }

    get landingPageDescription(): string {
        return this.landingPageContents?.description || '';
    }

    get corporationTitle() {
        if (this.landingPageContents) {
            switch (this.landingPageContents.parent) {
                case 'flights':
                    return 'هواپیمایی';
                case 'trains':
                    return 'ریلی';
                case 'buses':
                    return 'اتوبوسرانی';
            }
        }
        return undefined;
    }

    get filteredBanners(): SiteBanner[] {
        const currentSearchForm = useCurrentSearchForm<TripSearchForm>();
        const data = this.banners.filter(b => {
            if (typeof b.international === 'boolean') {
                if (b.international) {
                    return currentSearchForm.value.isForeign;
                } else {
                    return currentSearchForm.value.isDomestic;
                }
            } else {
                return true;
            }
        });
        return this.filterWhiteLabel(this.filterPrivacy(data));
    }

    get listBanners(): SiteBanner[] {
        return this.filteredBanners.filter(b => b.position === 'list');
    }

    get taxiBanners(): SiteBanner[] {
        return this.filteredBanners.filter(b => b.position === 'taxiBanner');
    }

    get hotelBanners(): SiteBanner[] {
        return this.filteredBanners.filter(b => b.position === 'hotelBanner');
    }

    get loadingBanners(): SiteBanner[] {
        return this.filteredBanners.filter(b => b.position === 'loading');
    }

    private filterPrivacy<T extends ContentLike>(data: T[]): T[] {
        if (import.meta.server) {
            return [];
        }
        const credentials: UserCredential[] = JSON.parse(
            localStorage.getItem(AuthLocalStorageKeys.credentials) || '[]'
        );
        const index: number = JSON.parse(
            localStorage.getItem(
                AuthLocalStorageKeys.selectedCredentialIndex
            ) || '0'
        );
        const loggedIn = !!credentials.length;
        const organizational =
            credentials[index]?.type === UserType.Organization;
        const currentPrivacy = !loggedIn
            ? BannerPrivacyEnum.Unauthorized
            : organizational
              ? BannerPrivacyEnum.Organizational
              : BannerPrivacyEnum.Normal;
        return data.filter(content =>
            [BannerPrivacyEnum.All, currentPrivacy].includes(
                content.privacy || BannerPrivacyEnum.All
            )
        );
    }

    private filterWhiteLabel<
        T extends { whitelabel?: string; whiteLabel?: string },
    >(data: T[]): T[] {
        const { whiteLabel } = useWhiteLabel();
        return data.filter(
            datum =>
                (datum.whitelabel || datum.whiteLabel || 'mrbilit') ===
                whiteLabel
        );
    }

    private filterNotClosed<T extends { id: string }>(data: T[]): T[] {
        return data.filter(datum => !this.closedMessageIds.includes(datum.id));
    }

    private filterPlatform<
        T extends {
            web?: boolean;
            androidWebApp?: boolean;
            iosWebApp?: boolean;
        },
    >(data: T[]): T[] {
        const { isIos, isAndroid, isLegacyAndroid, isLegacyIos } = useDevice();
        return data.filter(datum => {
            if (isAndroid.value || isLegacyAndroid.value) {
                return !!datum.androidWebApp;
            }
            if (isIos.value || isLegacyIos.value) {
                return !!datum.iosWebApp;
            }
            return !!datum.web;
        });
    }

    /**
     * client only
     */
    get filteredHomepageMessages(): SiteMessage[] {
        return this.filterNotClosed(
            this.filterWhiteLabel(this.filterPrivacy(this.homepageMessages))
        );
    }

    get filteredMessages(): SiteMessage[] {
        const currentSearchForm = useCurrentSearchForm<TripSearchForm>();
        const messages = this.messages.filter(message => {
            switch (message.searchMode) {
                case MessageSearchModeEnum.Both:
                    return true;
                case MessageSearchModeEnum.Domestic:
                    return currentSearchForm.value.isDomestic;
                case MessageSearchModeEnum.International:
                    return currentSearchForm.value.isForeign;
                default:
                    return true;
            }
        });
        return this.filterNotClosed(
            this.filterWhiteLabel(this.filterPrivacy(messages))
        );
    }

    get filteredFaqs(): Faq[] {
        return this.filterWhiteLabel(this.faqs);
    }

    get filteredTermsAndConditions(): TermsAndConditions[] {
        return this.filterWhiteLabel(this.termsAndConditions);
    }

    closeMessage(message: CommonMessageProperties) {
        if (!message.closeAction) {
            return;
        }
        if (this.closedMessageIds.includes(message.id)) {
            return;
        }
        this.closedMessageIds = [...this.closedMessageIds, message.id];
        if (message.closeAction === 'always') {
            const prevClosed = JSON.parse(
                localStorage.getItem('closedAlerts') || '[]'
            ) as string[];
            if (prevClosed.includes(message.id)) {
                return;
            }
            localStorage.setItem(
                'closedAlerts',
                JSON.stringify([...prevClosed, message.id])
            );
        }
    }

    readMessage(message: CommonMessageProperties) {
        if (message.mode !== 'once') {
            return;
        }
        if (this.closedMessageIds.includes(message.id)) {
            return;
        }
        this.closedMessageIds = [...this.closedMessageIds, message.id];
        const prevClosed = JSON.parse(
            localStorage.getItem('closedAlerts') || '[]'
        ) as string[];
        localStorage.setItem(
            'closedAlerts',
            JSON.stringify([...prevClosed, message.id])
        );
    }

    /**
     * Making sure the alert is not hidden by anyway other than destroying.
     * On showing alert, we add that to storage and next time add them to the closed message if not present.
     */
    shownMessage(message: CommonMessageProperties) {
        if (message.mode !== 'once') {
            return;
        }
        const prevShownMessage = JSON.parse(
            localStorage.getItem('shownAlerts') || '[]'
        ) as string[];
        localStorage.setItem(
            'shownAlerts',
            JSON.stringify([...prevShownMessage, message.id])
        );
    }

    init() {
        const prevShownAlerts = JSON.parse(
            localStorage.getItem('shownAlerts') || '[]'
        ) as string[];
        const prevClosedAlerts = JSON.parse(
            localStorage.getItem('closedAlerts') || '[]'
        ) as string[];
        const aggregate = [
            ...prevClosedAlerts,
            ...prevShownAlerts.filter(id => !prevClosedAlerts.includes(id)),
        ];
        localStorage.setItem('closedAlerts', JSON.stringify(aggregate));
        localStorage.removeItem('shownAlerts');
        this.closedMessageIds = aggregate;
    }

    get homepageFilteredBanners(): SiteTopBanner[] {
        return this.filterWhiteLabel(this.filterPrivacy(this.homepageBanners));
    }

    get homepageFilteredSliders(): SiteTopBanner[][] {
        return this.homepageSliders
            .map(sliders => this.filterWhiteLabel(this.filterPrivacy(sliders)))
            .filter(sliders => sliders.length);
    }

    get nextRandomMusic(): string {
        const list = this.musicList;
        const randomElement = list[Math.floor(Math.random() * list.length)];
        return randomElement?.src ?? '';
    }

    async loadUtmCampaigns(forceRefresh = false) {
        if (this.utmCampaigns.length && !forceRefresh) {
            return;
        }
        try {
            const { data } = await UtmCampaignApiUtmCampaignsGet();
            this.utmCampaigns = data
                .filter(campaign => !campaign.Disabled)
                .map(campaign => new SiteUtmCampaign(campaign));
        } catch (e) {
            reportException(e);
        }
    }

    async getFaqs() {
        const { data } = await FaqApiFaqsGet();
        this.faqs = data.map(faq => ({
            id: faq.id,
            FaqItem: faq.FaqItem,
            whitelabel: faq.whitelabel,
        }));
    }

    async getSearchResultContents(params?: {
        reserveType: ReserveType;
        destination: string;
        origin?: string;
        hotelName?: string;
    }) {
        const layoutStore = useLayoutStore();
        const searchFormStore = useSearchFormStore();
        const currentSearchForm = useCurrentSearchForm<TripSearchForm>();
        if (!layoutStore.isExtraFeaturesEnabled) {
            return;
        }
        const service = params?.reserveType ?? searchFormStore.reserveType;
        const { destination, hotelName } = params || searchFormStore.targetCode;
        const origin =
            params?.origin || currentSearchForm.value.originCode || '';
        const { lastLoadedSearchResultContents } = this;
        if (lastLoadedSearchResultContents) {
            if (
                lastLoadedSearchResultContents.origin === origin &&
                lastLoadedSearchResultContents.destination === destination &&
                lastLoadedSearchResultContents.service === service &&
                lastLoadedSearchResultContents.hotelName === hotelName
            ) {
                this.searchResultContentsLoading = false;
                return;
            }
        }
        try {
            this.searchResultContentsLoading = true;
            this.searchResultFaqs = [];
            this.searchResultContent = '';
            this.lastLoadedSearchResultContents = {
                service,
                destination,
                origin,
                hotelName,
            };
            let hasLandingContents: boolean;
            let content1: SearchResultContent | undefined;
            if (hotelName) {
                const {
                    data: { content, hasLandingContents: hLC },
                } = await SearchResultContentApiSearchResultContentsOrEmptyGet({
                    service,
                    origin,
                    destination,
                    hotelName,
                });
                hasLandingContents = hLC;
                content1 = content;
            } else {
                const {
                    data: { content, hasLandingContents: hLC },
                } =
                    await SearchResultContentApiSearchResultContentsFullContentGet(
                        {
                            service,
                            origin,
                            destination,
                        }
                    );
                hasLandingContents = hLC;
                content1 = content;
            }
            this.hasLandingContents = hasLandingContents ?? false;
            if (content1) {
                const {
                    faqs,
                    description,
                    CityBanner: cityBanner,
                    content,
                    redirectUrl,
                    redirectErrorCode,
                } = content1;
                if (import.meta.server && redirectUrl) {
                    throw new ViewRedirectError(
                        'Path not valid',
                        redirectUrl,
                        {},
                        redirectErrorCode ?? 301
                    );
                }
                this.searchResultFaqs = faqs || [];
                this.searchResultContent = content || '';
                this.searchResultDescription = description || '';
                this.hotelCityBanner = cityBanner
                    ? new CityBanner(cityBanner)
                    : null;
            }
        } catch (e) {
            if ((e as VerifyPathError).needRedirect) {
                throw e;
            }
            this.searchResultFaqs = [];
        } finally {
            this.searchResultContentsLoading = false;
        }
    }

    async getTermsAndCondition() {
        const { data } = await TermsAndConditionsApiTermsAndConditionsGet();
        this.termsAndConditions = data.map(term => ({
            id: term.id,
            whitelabel: term.whitelabel,
            body: term.body,
        }));
    }

    loadViewPageContents() {
        if (
            this.lastLoadedViewContent === null ||
            dayConfig
                .gregory(this.lastLoadedViewContent)
                .diff(new Date(), 'minutes') >= 10
        ) {
            this.loadMessages();
            this.lastLoadedViewContent = new Date();
        }
    }

    async loadBanners() {
        try {
            this.isLoadingBanners = true;
            const { data } = await BannerApiBannersGet();
            this.banners = this.filterPlatform(data).map(
                banner => new SiteBanner(banner)
            );
        } catch (e) {
            reportException(e);
        } finally {
            this.isLoadingBanners = false;
        }
    }

    async loadHomepageMessages(forceRefresh = false) {
        if (this.homepageMessagesLoaded && !forceRefresh) {
            return;
        }
        try {
            this.homepageMessagesLoading = true;
            const { data } = await HomepageApiHomepagesGet();
            const filteredData = this.filterPlatform(data);
            const sliders = filteredData.filter(
                datum =>
                    datum.data?.[0]?.__component ===
                    HomepageDataInnerOneOfComponentEnum.Slider
            );
            const messages = filteredData.filter(
                datum =>
                    datum.data?.[0]?.__component ===
                    HomepageDataInnerOneOfComponentEnum.Message
            );
            const banners = filteredData.filter(
                datum =>
                    datum.data?.[0]?.__component ===
                    HomepageDataInnerOneOfComponentEnum.Banner
            );
            this.homepageMessages = messages.map(
                message =>
                    new SiteMessage({
                        ...message,
                        id: message.id || message.data?.[0]?.id || '',
                        text: (message.data?.[0] as HomepageDataInnerOneOf)
                            .text,
                        closeAction: (
                            message.data?.[0] as HomepageDataInnerOneOf
                        ).closeAction,
                        type: (message.data?.[0] as HomepageDataInnerOneOf)
                            .mode,
                    })
            );
            this.homepageBanners = banners.map(
                banner =>
                    new SiteTopBanner({
                        ...banner,
                        id: banner.id || banner.data?.[0]?.id || '',
                        desktopImage: (
                            banner.data?.[0] as HomepageDataInnerOneOf1
                        )?.desktopImage,
                        mobileImage: (
                            banner.data?.[0] as HomepageDataInnerOneOf1
                        )?.image,
                        imageAlt: (banner.data?.[0] as HomepageDataInnerOneOf1)
                            ?.imageAlt,
                        link: (banner.data?.[0] as HomepageDataInnerOneOf1)
                            ?.link,
                    })
            );
            this.homepageSliders = sliders.map(
                sliderOuter =>
                    (
                        sliderOuter.data?.[0] as HomepageDataInnerOneOf2
                    )?.items?.map(
                        banner =>
                            new SiteTopBanner({
                                ...sliderOuter,
                                id:
                                    banner.id ||
                                    sliderOuter.id ||
                                    sliderOuter.data?.[0]?.id ||
                                    '',
                                desktopImage: banner.desktopImage,
                                mobileImage: banner.image,
                                imageAlt: banner?.imageAlt,
                                link: banner?.link,
                            })
                    ) || []
            );
            this.homepageMessagesLoading = false;
            this.homepageMessagesLoaded = true;
        } catch (e) {
            reportException(e);
        }
    }

    async loadMessages() {
        try {
            const { data } = await MessageApiMessagesGet();
            this.messages = this.filterPlatform(data).map(
                message => new SiteMessage(message)
            );
        } catch (e) {
            reportException(e);
        }
    }

    private async loadCorporations(service?: LandingContentsParentEnum) {
        switch (service) {
            case 'flights':
                return (await import('~/lib/static/flights_corporations.json'))
                    .default;
            case 'buses':
                return (await import('~/lib/static/buses_corporations.json'))
                    .default;
            case 'trains':
                return (await import('~/lib/static/trains_corporations.json'))
                    .default;
            default:
                return null;
        }
    }

    async getPageData(path: string) {
        const matches = path.match(/^\/([\w-]+)\/?([\w-/]+)?$/);
        const searchFormStore = useSearchFormStore();
        if (matches) {
            const parent = (matches[2] ? matches[1] : '_').replace('-', '_');
            const url: string | undefined = matches[2] || matches[1];
            if (
                this.landingPageContents?.parent === parent &&
                url === this.landingPageContents.url
            ) {
                return;
            }
            this.landingPageContentLoading = true;
            try {
                const { data } =
                    await LandingContentsApiLandingContentsDetailsGet({
                        url,
                        parent,
                    });
                this.landingPageContents = new LandingContent(data);
                if (data.corpIds?.length) {
                    try {
                        const corporations = await this.loadCorporations(
                            data.parent
                        );
                        this.corporations = data.corpIds
                            ?.slice(0, 6)
                            ?.map(
                                info =>
                                    new LandingCorporation(
                                        info,
                                        corporations?.serviceData.find(
                                            ({ id }) => id === info
                                        )?.name ?? ''
                                    )
                            )
                            .filter(info => info.name);
                    } catch (e) {
                        console.error(e);
                        this.corporations = [];
                    }
                }
                this.landingDistances =
                    data.distances
                        ?.slice(0, 6)
                        ?.map(distance => new LandingDistance(distance)) || [];
                this.landingSections =
                    data.Section?.map(section => new LandingSection(section)) ||
                    [];
                if (data.Providers) {
                    this.providers = data.Providers.map(
                        ({ image, ...rest }) => ({
                            image: image?.map(({ url, ...rest }) => ({
                                url: toAbsoluteSrc(url),
                                ...rest,
                            })),
                            ...rest,
                        })
                    );
                    this.providersTitle = data.ProvidersTitle || '';
                }
                if (data.defaultTab && process.server) {
                    searchFormStore.setReserveType(
                        data.defaultTab as ReserveType
                    );
                }
            } catch (e) {
                reportException(e);
                this.landingPageContents = null;
                this.corporations = [];
                this.landingDistances = [];
                this.landingSections = [];
            } finally {
                this.landingPageContentLoading = false;
            }
        } else {
            this.landingPageContents = null;
            this.corporations = [];
            this.landingDistances = [];
            this.landingSections = [];
        }
    }

    async getMusicList() {
        if (this.musicList.length) {
            return;
        }
        try {
            const { data } = await MusicListApiMusicListsGet();
            this.musicList = data.map(music => new SiteMusic(music));
        } catch (e) {
            reportException(e);
        }
    }

    async getAfterOrderMessage() {
        const { data } = await AfterOrderMessageApiAfterOrderMessageGet();
        this.afterOrderMessage = data.Enabled ? data.Message || '' : '';
    }
}

export const { use: useContentStore } = defineClassStore(
    'content',
    () => new ContentStore()
);
